// @ts-check
/* eslint-env browser */

import { reactiveStore } from '../reactive-store.js'

const storagePathToLayerId = new Map()
const previousCornerCache = new Map()

/**
 * Looks up overlay metadata by storage path.
 * NOTE: window.mapOverlays is the source of truth for overlay metadata.
 * @param {string} storagePath
 * @returns {object|null} Metadata object or null if not found
 */
function getOverlayMetadata (storagePath) {
  const overlays = window['mapOverlays'] || [] // eslint-disable-line dot-notation
  return overlays.find(m => m.storage_path === storagePath) || null
}

/**
 * Updates GCP geo coordinates after corner drag.
 * @param {string} storagePath
 * @param {[[number, number], [number, number], [number, number], [number, number]]} coordinates
 */
function updateFeatureCoordinates (storagePath, coordinates) {
  const metadata = getOverlayMetadata(storagePath)
  if (!metadata?.ground_control_points) {
    console.warn('No map metadata found for storage_path:', storagePath)
    return
  }

  for (let i = 0; i < Math.min(metadata.ground_control_points.length, coordinates.length); i++) {
    metadata.ground_control_points[i].geo = [...coordinates[i]]
  }

  // Trigger reactive update to notify subscribers
  const features = reactiveStore.features.get()
  reactiveStore.features.set(features, 'gcp-update')
}

/**
 * Persists updated GCPs to the GeoTIFF file via the backend API.
 * @param {string} storagePath
 */
function saveGCPsToBackend (storagePath) {
  if (!storagePath) {
    return
  }

  const metadata = getOverlayMetadata(storagePath)
  if (!metadata?.ground_control_points) {
    console.warn('No map metadata found for storage_path:', storagePath)
    return
  }

  fetch('/api/geotiffs/gcps', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ground_control_points: metadata.ground_control_points,
      storage_path: storagePath
    })
  }).then((response) => {
    if (!response.ok) return
    // Backend clears the map's reviewed_by/reviewed_at on a GCP update — reflect
    // that in the sidebar tag immediately so the UI matches the persisted state
    // (don't rely on selectedFeatureId, which may not match this map overlay).
    const details = document.querySelector(
      `details[data-entity-id="${storagePath}"]`
    )
    details?.querySelector('status-tag')?.setAttribute('data-complete', 'false')
  }).catch((error) => {
    console.error('Failed to save GCPs:', error)
  })
}
/**
 * Builds GeoTIFF overlay data from window.mapOverlays and overlay opacities.
 * @param {{ storagePath: string, opacity: number }[]} overlayMaps
 * @returns {Array<{ url: string, coordinates: [[number, number], [number, number], [number, number], [number, number]], storagePath: string, opacity: number }>}
 */
function buildGeoTIFFs (overlayMaps) {
  const overlays = window['mapOverlays'] || [] // eslint-disable-line dot-notation
  const opacityByStorage = new Map(
    overlayMaps.map((entry) => [entry.storagePath, entry.opacity])
  )

  return overlays.map((overlay) => {
    const storagePath = overlay.storage_path
    const opacity = opacityByStorage.has(storagePath)
      ? opacityByStorage.get(storagePath)
      : 0.7

    /** @type {[[number, number], [number, number], [number, number], [number, number]]} */
    const coordinates = overlay.ground_control_points.map(
      (gcp) => /** @type {[number, number]} */ ([gcp.geo[0], gcp.geo[1]])
    )

    return {
      url: `/api/geotiff-proxy?url=${encodeURIComponent(overlay.presigned_url)}`,
      coordinates,
      storagePath,
      opacity
    }
  })
}

/**
 * Moves GeoTIFF layers before Geoman layers so polygons appear on top
 * @param {import('maplibre-gl').Map} map
 */
export function moveGeoTIFFsBeforeGeoman (map) {
  if (!map) return

  const style = map.getStyle()

  // Find the first polygon layer to insert GeoTIFFs before
  // Prefer our custom overlay fill layer if it exists, otherwise fall back to original Geoman layers
  let targetLayer = style.layers?.find((layer) =>
    layer.id === 'gm_main_overlay_fill'
  )

  if (!targetLayer) {
    // Fall back to finding the first original Geoman layer with gm_main source
    targetLayer = style.layers?.find((layer) =>
      'source' in layer && layer.source === 'gm_main'
    )
  }

  if (!targetLayer) {
    return
  }

  // Collect all GeoTIFF layer IDs in order
  const geotiffLayerIds = []
  const styleLayers = style?.layers || []
  for (const layer of styleLayers) {
    if (layer.id?.startsWith('geotiff-layer-')) {
      geotiffLayerIds.push(layer.id)
    }
  }

  // Move each GeoTIFF layer before the target polygon layer
  // Move in forward order to maintain their relative order
  for (const layerId of geotiffLayerIds) {
    try {
      map.moveLayer(layerId, targetLayer.id)
    } catch (error) {
      console.warn('Failed to move GeoTIFF layer:', error)
    }
  }
}

/**
 * Adds GeoTIFF images to the map as image sources
 * @param {import('maplibre-gl').Map} map
 * @param {Array<{ url: string, coordinates: [[number, number], [number, number], [number, number], [number, number]], storagePath: string, opacity?: number }>} geotiffs
 */
function addGeoTIFFs (map, geotiffs) {
  geotiffs.forEach((geotiff, index) => {
    const sourceId = `geotiff-${index}`
    const layerId = `geotiff-layer-${index}`

    try {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId)
      }
    } catch (e) {}

    try {
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId)
      }
    } catch (e) {}

    const opacity = typeof geotiff.opacity === 'number' ? geotiff.opacity : 0.7
    const visible = opacity > 0

    map.addSource(sourceId, {
      type: 'image',
      url: geotiff.url,
      coordinates: geotiff.coordinates
    })

    map.addLayer({
      id: layerId,
      type: 'raster',
      source: sourceId,
      layout: {
        visibility: visible ? 'visible' : 'none'
      },
      paint: {
        'raster-opacity': opacity
      }
    })

    if (geotiff.storagePath) {
      storagePathToLayerId.set(geotiff.storagePath, layerId)
    }
  })
}

/**
 * Initialise all GeoTIFF overlays on the map based on window.mapOverlays and overlay opacities.
 * @param {import('maplibre-gl').Map} map
 * @param {{ storagePath: string, opacity: number }[]} overlayMaps
 */
export function initGeoTIFFs (map, overlayMaps) {
  if (!map) return

  storagePathToLayerId.clear()
  previousCornerCache.clear()

  const geotiffs = buildGeoTIFFs(overlayMaps)
  if (geotiffs.length > 0) {
    addGeoTIFFs(map, geotiffs)
    moveGeoTIFFsBeforeGeoman(map)
  }
}

/**
 * Updates the opacity of existing GeoTIFF overlays given the latest overlay opacities.
 * @param {import('maplibre-gl').Map} map
 * @param {{ storagePath: string, opacity: number }[]} overlayMaps
 */
export function updateGeoTIFFOpacities (map, overlayMaps) {
  if (!map) return
  overlayMaps.forEach(({ storagePath, opacity }) => {
    setGeoTIFFOpacity(map, storagePath, opacity)
  })
}

/**
 * Sets the opacity of a GeoTIFF overlay layer by storage_path.
 * @param {import('maplibre-gl').Map} map
 * @param {string} storagePath
 * @param {number} opacity 0–1
 */
export function setGeoTIFFOpacity (map, storagePath, opacity) {
  if (!map || !storagePath) return
  const layerId = storagePathToLayerId.get(storagePath)
  if (!layerId || !map.getLayer(layerId)) return
  map.setPaintProperty(layerId, 'raster-opacity', opacity)
  map.setLayoutProperty(layerId, 'visibility', opacity === 0 ? 'none' : 'visible')
}

/**
 * Clears the cached corner positions used for incremental SSD matching.
 */
export function clearOverlayCornerCache () {
  previousCornerCache.clear()
}

/**
 * @typedef {{ storagePath: string, geo: [number, number][] }} OverlayGcpSnapshot
 */

/**
 * Snapshots GCP geo coordinates for all map overlays.
 * @returns {OverlayGcpSnapshot[]}
 */
export function snapshotOverlayGcps () {
  const overlays = window['mapOverlays'] || [] // eslint-disable-line dot-notation
  return overlays.map((overlay) => ({
    storagePath: overlay.storage_path,
    geo: (overlay.ground_control_points || []).map((gcp) =>
      /** @type {[number, number]} */ ([gcp.geo[0], gcp.geo[1]])
    )
  }))
}

/**
 * Restores GCP geo coordinates from a history snapshot.
 * @param {OverlayGcpSnapshot[]|null|undefined} snapshot
 */
export function restoreOverlayGcps (snapshot) {
  if (!snapshot?.length) {
    return
  }

  const overlays = window['mapOverlays'] || [] // eslint-disable-line dot-notation
  for (const entry of snapshot) {
    const metadata = overlays.find(m => m.storage_path === entry.storagePath)
    if (!metadata?.ground_control_points) {
      continue
    }
    for (let i = 0; i < Math.min(metadata.ground_control_points.length, entry.geo.length); i++) {
      metadata.ground_control_points[i].geo = [...entry.geo[i]]
    }
  }
}

/**
 * Finds the dihedral permutation of polyCorners closest to previousCorners
 * by raw sum-of-squared-distances. Used for incremental tracking where
 * frame-to-frame changes are small and unambiguous.
 *
 * @param {[number, number][]} polyCorners - 4 current polygon corner positions
 * @param {[number, number][]} previousCorners - 4 previously matched positions
 * @returns {[[number, number], [number, number], [number, number], [number, number]]}
 */
export function matchBySSD (polyCorners, previousCorners) {
  const n = 4
  let bestOffset = 0
  let bestReversed = false
  let bestSSD = Infinity
  for (let offset = 0; offset < n; offset++) {
    for (const reversed of [false, true]) {
      let ssd = 0
      for (let j = 0; j < n; j++) {
        const k = reversed ? (offset - j + n) % n : (j + offset) % n
        ssd += (polyCorners[j][0] - previousCorners[k][0]) ** 2 +
               (polyCorners[j][1] - previousCorners[k][1]) ** 2
      }
      if (ssd < bestSSD) {
        bestSSD = ssd
        bestOffset = offset
        bestReversed = reversed
      }
    }
  }
  return /** @type {any} */ (Array.from({ length: n }, (_, i) =>
    bestReversed
      ? polyCorners[(bestOffset - i + n) % n]
      : polyCorners[(i - bestOffset + n) % n]
  ))
}

/**
 * Finds the permutation of polygon corners that best maps to the GCP positions
 * under a rigid-body transformation (rotation + translation).
 *
 * Geoman can shift the ring start index and reverse winding direction, giving
 * 8 dihedral permutations (4 cyclic x 2 directions). We use Procrustes fitting
 * to find the permutation most consistent with a single rotation, with a
 * smallest-rotation-angle tiebreaker for symmetric shapes.
 *
 * @param {[number, number][]} polyCorners - 4 current polygon corner positions
 * @param {[number, number][]} gcpCorners  - 4 previous GCP positions (target order)
 * @returns {[[number, number], [number, number], [number, number], [number, number]]}
 */
export function matchCornersToGCPs (polyCorners, gcpCorners) {
  const n = 4

  const rel = (pts) => {
    const cx = pts.reduce((s, p) => s + p[0], 0) / n
    const cy = pts.reduce((s, p) => s + p[1], 0) / n
    return pts.map(p => [p[0] - cx, p[1] - cy])
  }
  const oldRel = rel(gcpCorners)
  const newRel = rel(polyCorners)
  const sumSq = (pts) => pts.reduce((s, p) => s + p[0] ** 2 + p[1] ** 2, 0)
  const totalSq = sumSq(oldRel) + sumSq(newRel)

  let bestOffset = 0
  let bestReversed = false
  let bestResidual = Infinity
  let bestAbsTheta = Math.PI * 2

  for (let offset = 0; offset < n; offset++) {
    for (const reversed of [false, true]) {
      let dot = 0; let cross = 0
      for (let j = 0; j < n; j++) {
        const k = reversed ? (offset - j + n) % n : (j + offset) % n
        dot += oldRel[k][0] * newRel[j][0] + oldRel[k][1] * newRel[j][1]
        cross += oldRel[k][0] * newRel[j][1] - oldRel[k][1] * newRel[j][0]
      }
      const residual = totalSq - 2 * Math.sqrt(dot * dot + cross * cross)
      const absTheta = Math.abs(Math.atan2(cross, dot))
      if (residual < bestResidual - 1e-10 ||
        (residual < bestResidual + 1e-10 && absTheta < bestAbsTheta - 1e-10)) {
        bestResidual = residual
        bestAbsTheta = absTheta
        bestOffset = offset
        bestReversed = reversed
      }
    }
  }

  return /** @type {any} */ (Array.from({ length: n }, (_, i) =>
    bestReversed
      ? polyCorners[(bestOffset - i + n) % n]
      : polyCorners[(i - bestOffset + n) % n]
  ))
}

/**
 * Sync GeoTIFF image footprint + GCPs from editable overlay quad polygons.
 * Overlay polygons are expected to have `properties._isMapOverlay` and `properties._id === storage_path`.
 * @param {import('maplibre-gl').Map} map
 * @param {Array<any>} features
 * @param {boolean} [persist=false] - When true, saves updated GCPs to the reactive store and backend
 * @param {object} [rotationContext=null] - Rotation state captured before applyAccumulatedRotation clears it
 * @param {boolean} [undoRedo=false] - When true, align polygon corners to restored GCPs via SSD (skip Procrustes rematch)
 */
export function syncGeoTIFFOverlaysFromFeatures (map, features, persist = false, rotationContext = null, undoRedo = false) {
  if (!map || !features?.length) {
    return
  }

  const overlayFeatures = features.filter(f => f?.properties?._isMapOverlay && f?.geometry?.type === 'Polygon')
  overlayFeatures.forEach((overlayFeature) => {
    const storagePath = overlayFeature?.properties?._id
    if (!storagePath) {
      return
    }

    const layerId = storagePathToLayerId.get(storagePath)
    if (!layerId) {
      if (persist) {
        console.warn('[syncGeoTIFF] No layer ID found for:', storagePath)
      }
      return
    }

    const index = parseInt(layerId.replace('geotiff-layer-', ''), 10)
    const sourceId = `geotiff-${index}`
    const source = map.getSource(sourceId)
    const imageSource = /** @type {any} */ (source)
    if (!imageSource || typeof imageSource.setCoordinates !== 'function') {
      if (persist) {
        console.warn('[syncGeoTIFF] Invalid image source for:', sourceId)
      }
      return
    }

    const ring = overlayFeature?.geometry?.coordinates?.[0]
    if (!Array.isArray(ring) || ring.length < 4) return

    const polyPts = ring.length >= 5 && ring[0]?.[0] === ring[ring.length - 1]?.[0] && ring[0]?.[1] === ring[ring.length - 1]?.[1]
      ? ring.slice(0, -1)
      : ring
    const polyCorners = polyPts.slice(0, 4).map((pt) => /** @type {[number, number]} */ ([pt[0], pt[1]]))
    if (polyCorners.length < 4) return

    const metadata = getOverlayMetadata(storagePath)
    if (!metadata?.ground_control_points) return

    const gcpCorners = metadata.ground_control_points.map((gcp) =>
      /** @type {[number, number]} */ ([gcp.geo[0], gcp.geo[1]])
    )
    if (gcpCorners.length !== 4) return

    // Use passed rotation context (set during active rotation or after gm:rotateend)
    const rotState = rotationContext

    let corners
    if (undoRedo) {
      corners = matchBySSD(polyCorners, gcpCorners)
      previousCornerCache.set(storagePath, corners)
    } else if (rotState && Math.abs(rotState.totalAngle) > 1e-8) {
      // During active rotation or just after gm:rotateend: use the polygon corners directly
      // (Geoman has already rotated them to the correct position)
      const cached = previousCornerCache.get(storagePath)
      if (cached) {
        // Use greedy nearest-neighbour to map cached corners to current polygon corners
        // This preserves corner correspondence across rotation frames
        const used = new Set()
        corners = cached.map((cachedCorner) => {
          let closestIdx = -1
          let closestDist = Infinity
          for (let i = 0; i < polyCorners.length; i++) {
            if (used.has(i)) continue
            const dist = Math.hypot(
              polyCorners[i][0] - cachedCorner[0],
              polyCorners[i][1] - cachedCorner[1]
            )
            if (dist < closestDist) {
              closestDist = dist
              closestIdx = i
            }
          }
          used.add(closestIdx)
          return polyCorners[closestIdx]
        })
      } else {
        // No cache: establish initial corner correspondence using Procrustes
        corners = matchCornersToGCPs(polyCorners, gcpCorners)
      }
      // Always update cache during rotation to preserve corner ordering for next frame
      previousCornerCache.set(storagePath, corners)
    } else {
      // Normal operation: use cache or Procrustes
      const cached = previousCornerCache.get(storagePath)
      corners = cached
        ? matchBySSD(polyCorners, cached)
        : matchCornersToGCPs(polyCorners, gcpCorners)
      previousCornerCache.set(storagePath, corners)
    }

    imageSource.setCoordinates(corners)

    if (!persist) {
      return
    }

    const selectedOverlayId = reactiveStore.selectedFeatureId.get()
    if (selectedOverlayId && storagePath !== selectedOverlayId) return

    updateFeatureCoordinates(storagePath, corners)
    saveGCPsToBackend(storagePath)
  })
}
